import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Input and Textarea — the one text field for Student, Manager and Admin
 * (ADR-0088 D2).
 *
 * Native elements, like Button: a text box has no behavior to borrow from a
 * headless library (ADR-0088 D3). They replace Admin's field, Web's unused
 * shadcn input, the auth pages' class strings, the landing page's form field,
 * the identity form's `AccInput`, the survey's and FAQ search's inline
 * styles, and Manager's sign-in, search and filter CSS.
 *
 * The size carries the shape (`field-shape.ts`): `sm` and `default` are
 * Admin's dense field, `lg` is the customer field. `aria-invalid` turns the
 * border to the danger color. `data-slot="input"` and `data-size` are the
 * hooks an app stylesheet uses to adjust a field in one layout.
 */
import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';
import { cn } from './cn.js';
import { fieldBase, fieldCompounds, fieldHeight, fieldSize, fieldSurface, textareaPadding } from './field-shape.js';
const fieldVariants = cva(fieldBase, {
    variants: { surface: fieldSurface, size: fieldSize },
    compoundVariants: [...fieldCompounds],
    defaultVariants: { size: 'default', surface: 'inset' },
});
/**
 * The field classes for a size and surface (inset unless raised), for any
 * field-shaped native control. Heights are the control's own.
 */
export function inputVariants({ surface, size = 'default' } = {}) {
    return fieldVariants({ size, surface });
}
export const Input = forwardRef(({ className, surface, size = 'default', ...props }, ref) => (_jsx("input", { ref: ref, "data-slot": "input", "data-size": size, className: cn(inputVariants({ surface, size }), fieldHeight[size ?? 'default'], className), ...props })));
Input.displayName = 'Input';
export const Textarea = forwardRef(({ className, surface, size = 'default', ...props }, ref) => (_jsx("textarea", { ref: ref, "data-slot": "textarea", "data-size": size, 
    // The field shapes pad only the sides of a single line; a text area
    // also needs the vertical padding.
    className: cn(inputVariants({ surface, size }), textareaPadding[size ?? 'default'], 'resize-y', className), ...props })));
Textarea.displayName = 'Textarea';
//# sourceMappingURL=input.js.map